<script lang="ts" setup>
import { computed } from 'vue';
import { NAlert, NButton, NModal, NTag } from 'naive-ui';
import { useI18n } from 'vue-i18n';
import { usePlanRunStore } from '@/stores/trajectory/plan-run-store';
import { planFailureReasonKey, PlanRunFailureProfile, type TrajectoryPlanFailureReason } from '@/constants/plan-failure-reason';
import VehicleIdentifier from '@/components/vehicle-identifier/VehicleIdentifier.vue';
import type { PlanRunUnplannedTrajectory } from '@/models/plan-run/plan-run';
import '@/assets/scss/variables/_variables.css';

const { t } = useI18n();
const planRunStore = usePlanRunStore();

const summary = computed(() => planRunStore.summary);

/**
 * De kop. Een gedeeltelijk geslaagde run is een resultaat en geen fout, dus "Plan gereed" met de verhouding
 * erbij. Kwam er niets uit, dan is dat óók een resultaat: met deze voertuigen en deze datum viel er niets te
 * plannen.
 */
const title = computed(() => {
  if (!summary.value) {
    return '';
  }

  return summary.value.createdTransportCount === 0
    ? t('components.transport.dayplanner.plan_run.summary.title_nothing_planned')
    : t('components.transport.dayplanner.plan_run.summary.title', {
      planned: summary.value.plannedTrajectoryCount,
      total: summary.value.trajectoryCount,
    });
});

/**
 * De inleiding als losse zinnen, elk met hun eigen telwoord. In één zin met twee getallen kan vue-i18n maar
 * op één ervan de meervoudsvorm kiezen, en dan staat er "Er is 3 rit aangemaakt".
 */
const intro = computed(() => {
  if (!summary.value) {
    return '';
  }

  const base = 'components.transport.dayplanner.plan_run.summary';

  if (summary.value.createdTransportCount === 0) {
    return t(`${base}.intro_nothing_planned`);
  }

  const transports = summary.value.createdTransportCount;
  const unplanned = summary.value.unplannedTrajectories.length;

  return [
    t(`${base}.intro_transports`, { count: transports }, transports),
    unplanned > 0 ? t(`${base}.intro_unplanned`, { count: unplanned }, unplanned) : '',
  ].filter(Boolean).join(' ');
});

const isEstimated = computed(() =>
  summary.value?.failureProfile === PlanRunFailureProfile.Estimated && (summary.value?.estimatedLegCount ?? 0) > 0);

/** Op reden gegroepeerd: zo ziet de planner in één oogopslag of het aan de vloot of aan de vensters lag. */
const groups = computed(() => {
  const byReason = new Map<TrajectoryPlanFailureReason, PlanRunUnplannedTrajectory[]>();

  for (const trajectory of summary.value?.unplannedTrajectories ?? []) {
    const group = byReason.get(trajectory.reason);
    if (group) {
      group.push(trajectory);
    } else {
      byReason.set(trajectory.reason, [trajectory]);
    }
  }

  return [...byReason.entries()].map(([reason, trajectories]) => ({
    reason,
    reasonLabel: t(planFailureReasonKey(reason)),
    trajectories,
  }));
});

const describeVehicle = (trajectory: PlanRunUnplannedTrajectory): string =>
  [trajectory.brand, trajectory.model].filter(Boolean).join(' ');

/** Dezelfde pijl als elders bij een route in een dialoog (zie AbortTransportModal). */
const describeRoute = (trajectory: PlanRunUnplannedTrajectory): string =>
  [trajectory.pickUpCity, trajectory.dropOffCity].filter(Boolean).join(' → ');

/**
 * De dialoog-preset van naive-ui zet zijn eigen breedte; die is alleen via de style-prop te overschrijven,
 * niet met een klasse. Breder dan een bevestigingsdialoog, want per regel staan kenteken, merk en route naast
 * elkaar en bij een smallere dialoog breken plaatsnamen middenin af.
 */
const modalStyle = { width: '640px', maxWidth: 'calc(100vw - 48px)' };

const close = () => planRunStore.acknowledgeSummary();
</script>

<template>
<!-- Zelfde vorm als de bevestigingsdialogen elders in de app: preset "dialog", geen icoon, quaternary knop. -->
<n-modal
    :show="planRunStore.showSummary"
    preset="dialog"
    class="confirmation-modal plan-run-summary-modal"
    :show-icon="false"
    :bordered="false"
    :title="title"
    closable
    :style="modalStyle"
    @close="close"
    @mask-click="close">
    <div v-if="summary" class="plan-run-summary">
        <p class="plan-run-summary__intro">{{ intro }}</p>

        <!--
          Profiel Q staat bovenaan en niet bij de trajecten: dit gaat over de ritten die er wél zijn, en de
          planner moet het weten vóórdat hij ze definitief plant.
        -->
        <n-alert v-if="isEstimated" type="warning" :bordered="false" :show-icon="true">
            {{ t('components.transport.dayplanner.plan_run.summary.estimated', {
              count: summary.estimatedLegCount,
              total: summary.totalLegCount,
            }) }}
        </n-alert>

        <div v-if="groups.length > 0" class="plan-run-summary__groups">
            <section v-for="group in groups" :key="group.reason" class="plan-run-summary__group">
                <header class="plan-run-summary__reason">
                    <span class="plan-run-summary__reason-text">{{ group.reasonLabel }}</span>
                    <n-tag size="small" :bordered="false" round>{{ group.trajectories.length }}</n-tag>
                </header>

                <ul class="plan-run-summary__list">
                    <li v-for="trajectory in group.trajectories" :key="trajectory.trajectoryId">
                        <!-- De kentekenweergave van de app: plaat per land, en zelf al een link naar de opdracht. -->
                        <vehicle-identifier
                            class="plan-run-summary__plate"
                            :license-plate="trajectory.licensePlate"
                            :chassis-number="trajectory.chassisNumber ?? undefined"
                            :country="trajectory.country ?? undefined"
                            size="small"
                            :has-link-to-assignment="true"
                            :assignment-id="trajectory.assignmentId" />

                        <span class="plan-run-summary__vehicle">{{ describeVehicle(trajectory) }}</span>
                        <span class="plan-run-summary__route">{{ describeRoute(trajectory) }}</span>

                        <!--
                          Handelde een collega dit traject tijdens de run af, dan benoemen we dat: anders gaat
                          de planner zoeken naar iets dat niet meer op Te plannen staat.
                        -->
                        <n-tag v-if="!trajectory.isStillToPlan" size="small" :bordered="false">
                            {{ t('components.transport.dayplanner.plan_run.summary.no_longer_to_plan') }}
                        </n-tag>
                    </li>
                </ul>
            </section>
        </div>

        <!--
          Alleen wanneer er ook echt iets te zien is. Zijn de trajecten inmiddels verdwenen, dan is "deze
          trajecten staan nog op Te plannen" een uitspraak over een lege lijst.
        -->
        <p v-if="groups.length > 0" class="plan-run-summary__footnote">
            {{ t('components.transport.dayplanner.plan_run.summary.footnote') }}
        </p>
    </div>

    <template #action>
        <n-button quaternary size="small" @click="close">
            {{ t('core.close') }}
        </n-button>
    </template>
</n-modal>
</template>

<style lang="scss" scoped>
/*
  Opbouw als de andere lijst-in-een-dialoog van de app (zie AbortTransportModal): elke regel is een blokje op
  een lichte achtergrond, zonder scheidslijnen. Lijnen tussen dertig regels geven een hek-effect; met een
  achtergrond doet de witruimte het scheiden en blijven de kentekenplaten het zwaarste element.
*/
.plan-run-summary {
    display: flex;
    flex-direction: column;
    gap: var(--dis-space-2_0x);
    /* Lucht tussen de titel en de eerste regel; de dialoog zet daar zelf vrijwel niets. */
    padding-top: var(--dis-space-1_0x);

    &__intro {
        margin: 0;
        color: var(--dis-color-text-body);
        font-size: var(--dis-font-size-medium);
    }

    /*
      De lijst scrollt, de inleiding en de voetnoot niet: bij een run over veertig auto's liep de dialoog
      anders van het scherm af en was de sluitknop niet meer te bereiken. Een vaste bovengrens houdt het
      ritme rustig, ook wanneer er maar één traject overblijft.
    */
    &__groups {
        display: flex;
        flex-direction: column;
        gap: var(--dis-space-2_0x);
        max-height: 42vh;
        overflow-y: auto;
    }

    &__group {
        display: flex;
        flex-direction: column;
    }

    /* De reden is de kop van zijn groep; het aantal staat er direct achter en niet los aan de rechterkant. */
    &__reason {
        display: flex;
        align-items: center;
        gap: var(--dis-space-1_0x);
        padding-bottom: var(--dis-space-1_0x);
    }

    &__reason-text {
        color: var(--dis-color-text-body);
        font-size: var(--dis-font-size-medium);
        font-weight: var(--dis-font-weight-strong);
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: var(--dis-space-0_5x);
        margin: 0;
        padding: 0;
        list-style: none;

        li {
            display: flex;
            align-items: center;
            gap: var(--dis-space-1_5x);
            padding: var(--dis-space-1_0x);
            border-radius: var(--dis-shape-height-0_5x);
            background-color: var(--dis-color-surface-secondary);
            font-size: var(--dis-font-size-medium);
        }
    }

    /* Vaste breedte: zo staan merk en route van alle regels onder elkaar in plaats van te verspringen. */
    &__plate {
        flex: 0 0 auto;
        min-width: 104px;
    }

    &__vehicle {
        flex: 0 0 auto;
        min-width: 150px;
        color: var(--dis-color-text-body);
    }

    &__route {
        margin-right: auto;
        color: var(--dis-color-text-label);
    }

    &__footnote {
        margin: 0;
        color: var(--dis-color-text-label);
        font-size: var(--dis-font-size-medium);
    }
}
</style>
